import {
  ExclamationCircleIcon as ExclamationIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useUnlockWorkflow } from "../api/WorkflowApi";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";

interface ReportUnlockModalProps {
  positiveAction: boolean | undefined;
  title: string;
  message: string;
  onCancel: () => void;
  onOk: () => void;
}

const ReportUnlockModal = ({
  positiveAction,
  title,
  message,
  onCancel,
  onOk,
}: ReportUnlockModalProps) => {
  return (
    <>
      <div className="flex gap-4">
        {!positiveAction ? (
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-red-100">
            <ExclamationIcon className="w-7 text-red-600" />
          </div>
        ) : (
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-green-100">
            <LockOpenIcon className="w-7 text-green-600" />
          </div>
        )}
        <div>
          <div className="pb-1 font-bold capitalize">{title}</div>
          <div className="text-sm text-gray-500">{message}</div>
        </div>
      </div>
      <div className="flex justify-end mt-4 gap-4">
        <Button
          text={"Cancel"}
          onClick={onCancel}
          colorType={ButtonColorTypes.cancel}
        ></Button>
        <Button
          text={positiveAction ? "Ok" : "Exit"}
          colorType={
            positiveAction
              ? ButtonColorTypes.primary
              : ButtonColorTypes.secondary
          }
          className="w-28"
          onClick={onOk}
        ></Button>
      </div>
    </>
  );
};

export const useReportUnlock = () => {
  const { mutate, error, isLoading, isSuccess, reset } = useUnlockWorkflow();
  const [successHandler, setSuccessHandler] = useState<{
    handler: () => void;
  }>();
  const { modal, show, hide } = useConfirmationDialog();
  const { canUnlock } = usePermissionValidator();

  function performUnlock(
    workflowRunUUID: string,
    title: string,
    message: string,
    afterSuccess: () => void,
    positiveAction?: boolean,
    forceUnlock?: boolean,
    bypassUnlockPermission?: boolean
  ) {
    if (forceUnlock) {
      if (canUnlock || bypassUnlockPermission)
        mutate({
          workflowRunUUID: workflowRunUUID,
          success: positiveAction ? true : false,
        });
      afterSuccess();
      return;
    }
    if (!canUnlock && !bypassUnlockPermission) {
      return;
    }
    show(
      "",
      "",
      (action: ConfirmDialogActions) => {
        if (action === "close") {
          hide();
        }
      },
      <ReportUnlockModal
        positiveAction={positiveAction}
        title={title}
        message={message}
        onCancel={hide}
        onOk={() => {
          if (!canUnlock && !bypassUnlockPermission) {
            afterSuccess();
            return;
          }
          mutate({
            workflowRunUUID: workflowRunUUID,
            success: positiveAction ? true : false,
          });
          setSuccessHandler({ handler: afterSuccess });
        }}
      />,
      false
    );
  }

  useEffect(() => {
    if (!isSuccess) return;

    if (isSuccess && !isLoading && successHandler) {
      hide();
      reset();
      successHandler.handler?.();
    }
  }, [isSuccess, isLoading, successHandler, reset, hide]);

  return { error, performUnlock, isLoading, modal };
};
